import { AddRounded, ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Grid, IconButton, Tooltip } from "@mui/material";
import { Fragment, SyntheticEvent, useCallback, useEffect, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import { CBCreationOfProps, CBRoleKey, IFormValues } from "../../types";
import { defaultBuagClassesState, isGlobalEditModeState } from "../../state/customerbaseState";
import CBAccordionTitle from "../CBAccordionTitle";
import UpdateBtnsAccordion from "../UpdateBtnsAccordion";
import CBPaymentAccordion from "./CBPaymentAccordion";
import { getCountryByCode } from "../../utils";
import { getSapConfigurationByCountry } from "../../api/service/customerbaseApi";


const CustomerBasePaymentInfo = ({creationOf} : CBCreationOfProps) => {
    const { t } = useTranslation('customerbase');
    const isCreationMode = !!creationOf;
    const { control, getValues, formState }  = useFormContext<IFormValues>();
    const [isExpanded, setIsExpanded] = useState(false);
    const isGlobalEditMode = useRecoilValue(isGlobalEditModeState);
    const editing = isGlobalEditMode;
    const paymentsFieldArray = useFieldArray<IFormValues, 'cbPaymentInfo'>({
        name: "cbPaymentInfo",
        control: control
    });
   
    const roleWatch = useWatch<IFormValues, 'cbDetails.role'>({name: "cbDetails.role"});
    const cbIdWatch = useWatch<IFormValues, 'cbDetails.cbCustomerId'>({name: "cbDetails.cbCustomerId"});

    const [filteredBuagClasses, setFilteredBuagClasses] = useState<string[]>([]);
    const [defaultBuagClasses, setDefaultBuagClasses] = useRecoilState(defaultBuagClassesState);

    const appendNewPayment = () => {
        const newPayment = {
            buagClass: "",
            paymentMethod: "",
            paymentTerms: "",
            correspondingVariant: "",
            iban: "",
            accountHolder: "",
            referenceDetails: ""
        };
        paymentsFieldArray.append(newPayment);
    }

    const onAdd = (event: SyntheticEvent) => {
        event.stopPropagation();
        appendNewPayment();
    }

    const onRemove = (event: SyntheticEvent, index: number) => {
        event.stopPropagation();
        paymentsFieldArray.remove(index);
    }

    /*
        Nel caso di utente già salvato, con ruolo sold to party e quindi almeno un buag disponibile, non serve alcun blocco (si lascia la gestione più libera)
    */
    const bypassCreationBuagBlock = useCallback(() => {
        return !!getValues("cbDetails.cbCustomerId") && formState.defaultValues?.cbDetails?.role?.includes(CBRoleKey.SOLD_TO_PARTY);

    }, [getValues("cbDetails.cbCustomerId"), formState.defaultValues?.cbDetails?.role])

    useEffect(() => { 
        if(!bypassCreationBuagBlock() && paymentsFieldArray.fields.length > 0 && !roleWatch.includes(CBRoleKey.SOLD_TO_PARTY)) {
            paymentsFieldArray.replace([]);
        }
          
    },[roleWatch, isCreationMode, cbIdWatch]);

    useEffect(() => {

        if(isCreationMode && !getValues("cbDetails.cbCustomerId")) return;

        if(defaultBuagClasses.length == 0) {
            setDefaultBuagClasses(paymentsFieldArray.fields);

            let bg: string[] = []
            paymentsFieldArray.fields.forEach((buag, index) => {
                if(!bg.includes(buag.buagClass)) {
                    if(buag.buagClass == "")
                        bg.unshift(buag.buagClass);
                    else if(!!getValues(`salesAreas.${index}.id`))
                        bg.push(buag.buagClass);
                }
            });
            setFilteredBuagClasses(bg);
        }

    }, [isCreationMode, paymentsFieldArray.fields]);

    return (
        <Accordion expanded={Boolean(isExpanded)} onChange={(_e, expanded) => setIsExpanded(expanded)}>
            <AccordionSummary expandIcon={ <ExpandMore/>}>
                { isCreationMode ? 
                    <Grid container alignItems="center" width={"98%"}>
                        <Grid item md={6} xs={6}>
                            <CBAccordionTitle
                                section="cbPaymentInfo"
                                label={t("tabs.accordionsLabels.cbPaymentInfoDetails")}
                            />
                        </Grid>
                        <Grid item container justifyContent={"flex-end"} md={6} xs={6}>
                            <Grid item>
                                <Tooltip title={(isExpanded && !bypassCreationBuagBlock() && !roleWatch.includes(CBRoleKey.SOLD_TO_PARTY)) ? t("tooltips.customer.buagRoleRequired") : ""} arrow>
                                    <span>
                                        <IconButton
                                            color={"accent"}
                                            disabled={!isExpanded || (!bypassCreationBuagBlock() && !roleWatch.includes(CBRoleKey.SOLD_TO_PARTY))}
                                            onClick={onAdd}
                                        >
                                            <AddRounded/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                    :
                    <UpdateBtnsAccordion
                        section="cbPaymentInfo"
                        label={t("tabs.accordionsLabels.cbPaymentInfoDetails")}
                        editing={editing}
                        isExpanded={isExpanded}
                        showAdd={editing}
                        disableAdd={(!bypassCreationBuagBlock() && !roleWatch.includes(CBRoleKey.SOLD_TO_PARTY))}
                        tooltipTitleAddIcon={(isExpanded && !bypassCreationBuagBlock() && !roleWatch.includes(CBRoleKey.SOLD_TO_PARTY)) ? t("tooltips.customer.buagRoleRequired") : ''}
                        onAdd={onAdd}
                    />
                }
            </AccordionSummary>
            <AccordionDetails>
                <Grid container rowSpacing={1}>
                    { (isCreationMode && !getValues("cbDetails.cbCustomerId")) ?

                        paymentsFieldArray.fields.map((payment, index) => (
                           <Grid item md={12} xs={12} key={`payment-detail-${payment.id}`}>
                                <CBPaymentAccordion
                                    creationOf={creationOf}
                                    index={index}
                                    isExpanded={isExpanded}
                                    onRemove={onRemove}
                                />
                           </Grid>
                        ))
                    :
                        <>
                            {
                                paymentsFieldArray.fields.map((payment, index) => (
                                    (!getValues(`cbPaymentInfo.${index}.id`)) &&
                                    <Grid item xs={12} key={`payment-detail-${payment.id}`}>
                                         <CBPaymentAccordion
                                             creationOf={creationOf}
                                             index={index}
                                             isExpanded={isExpanded}
                                             onRemove={onRemove}
                                         />
                                    </Grid>
                                 ))
                            }
                            {
                                filteredBuagClasses.map((bg) => (
                                    <Fragment key={`fragment-buag-class-country-${bg}`}>
                                        <Grid item xs={12} key={`buag-class-country-${bg}`}>
                                            {!!bg &&
                                                <Accordion>
                                                    <AccordionSummary expandIcon={ <ExpandMore/>}>
                                                        {t(`labels.country.${getCountryByCode(bg)}`, {defaultValue: bg})}
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <Grid container rowSpacing={1}>
                                                            {
                                                                paymentsFieldArray.fields.map((payment, index) => (
                                                                    (!!getValues(`cbPaymentInfo.${index}.id`) && defaultBuagClasses[index]?.buagClass == bg) &&
                                                                    <Grid item xs={12} key={`payument-detail-${payment.id}`}>
                                                                        <CBPaymentAccordion 
                                                                            isExpanded={isExpanded}
                                                                            index={index}
                                                                            creationOf={creationOf}
                                                                            onRemove={onRemove}
                                                                        />
                                                                    </Grid>
                                                                ))

                                                            }
                                                        </Grid>
                                                    </AccordionDetails>
                                                </Accordion>
                                            }
                                        </Grid>
                                    </Fragment>
                                ))
                            }
                        </>
                    }
                </Grid>
            </AccordionDetails>
        </Accordion>
    )
}

export default CustomerBasePaymentInfo;